// components/ui/voiceButton.tsx
import React, { useRef, useState, useEffect } from "react";
import { Tooltip } from "@mui/material";
import Icon from "./Icon"; // adjust path as needed
import { toast } from "react-toastify";

interface VoiceRecognitionButtonProps {
  onResult?: (text: string) => void;
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  style?: React.CSSProperties;
}

const VoiceRecognitionButton: React.FC<VoiceRecognitionButtonProps> = ({
  onResult,
  onClick,
  disabled = false,
  style,
}) => {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  const startListening = () => {
    if (!("webkitSpeechRecognition" in window) && !("SpeechRecognition" in window)) {
      toast.error("Sorry, your browser does not support Speech Recognition.");
      return;
    }
    // Cancel any existing recognition session
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.lang = "en-US";
    recognitionRef.current.continuous = false;
    recognitionRef.current.interimResults = false;

    recognitionRef.current.onresult = (event: any) => {
      let transcript = event.results[0][0].transcript;
      // Make the first letter uppercase.
      transcript = transcript.charAt(0).toUpperCase() + transcript.slice(1);
      if (onResult) {
        onResult(transcript);
      }
      setIsListening(false);
    };

    recognitionRef.current.onerror = (event: any) => {
      toast.error("Speech recognition error. Check your microphone and try again");
      setIsListening(false);
    };

    recognitionRef.current.onend = () => {
      setIsListening(false);
    };

    setIsListening(true);
    recognitionRef.current.start();
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (onClick) {
      // Used for view mode to start edit mode
      onClick(e);
    } else if (onResult) {
      // Used in edit mode to directly start listening
      startListening();
    }
  };

  return (
    <Tooltip title={isListening ? "Listening..." : "Voice input"}>
      <div
        className={`p-1 rounded-full cursor-pointer transition-colors
          ${isListening ? "bg-blue-100 text-blue-600" : "hover:bg-gray-100"}`}
        style={{
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? "none" : "auto",
          ...style,
        }}
        onClick={disabled ? undefined : handleClick}
      >
        <Icon icon="mic" />
      </div>
    </Tooltip>
  );
};

export default VoiceRecognitionButton;
